import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStatus } from '@/hooks/useApi';
import { LoadingScreen } from '@/components/ui/LoadingSpinner';
import { HideableRoute, ModuleRoute } from '@/components/ModuleGuard';
import { ConfirmProvider } from '@/components/kit';
import Layout from '@/components/layout/Layout';
import { SubsidiaryProvider } from '@/contexts/SubsidiaryContext';
import { RoleViewProvider } from '@/contexts/RoleViewContext';
import { PeriodProvider } from '@/contexts/PeriodContext';
import ErrorBoundary from '@/components/ErrorBoundary';
import ProtectedRoute from '@/components/auth/ProtectedRoute';
import { ModuleProvider } from '@/contexts/ModuleContext';
import IntegrationShell, { isIntegrationOnly } from '@/components/layout/IntegrationShell';
import SessionTimeoutWarning from '@/components/auth/SessionTimeoutWarning';

// Page imports (lazy loaded for code splitting)
const Dashboard = React.lazy(() => import('@/pages/Dashboard'));
const Approvals = React.lazy(() => import('@/pages/Approvals'));
const AccountsMatrix = React.lazy(() => import('@/pages/AccountsMatrix'));
const Reconciliation = React.lazy(() => import('@/pages/Reconciliation'));
const BalanceSheet = React.lazy(() => import('@/pages/BalanceSheet'));
const ReconciliationDetail = React.lazy(() => import('@/pages/ReconciliationDetail'));
const Login = React.lazy(() => import('@/pages/Login'));
const AcceptInvitation = React.lazy(() => import('@/pages/AcceptInvitation'));
const AuthCallback = React.lazy(() => import('@/pages/AuthCallback'));
const ChangePasswordRequired = React.lazy(() => import('@/pages/ChangePasswordRequired'));
const SignInSecurity = React.lazy(() => import('@/pages/admin/SignInSecurity'));
const MailAllowedDomains = React.lazy(() => import('@/pages/admin/MailAllowedDomains'));
const AuditPacks = React.lazy(() => import('@/pages/admin/AuditPacks'));
const EmailLabels = React.lazy(() => import('@/pages/admin/EmailLabels'));
const CashappAutomation = React.lazy(() => import('@/pages/admin/CashappAutomation'));
const GroupCompanies = React.lazy(() => import('@/pages/admin/GroupCompanies'));
const CreditControllers = React.lazy(() => import('@/pages/admin/CreditControllers'));
const CustomerRequestSettings = React.lazy(() => import('@/pages/admin/CustomerRequestSettings'));
const FeedHealthPage = React.lazy(() => import('@/pages/cashapp/FeedHealth'));   // 2 Nov: admins only (Davin)
const AccessReviewList = React.lazy(() => import('@/pages/admin/AccessReviews'));
const AccessReviewDetail = React.lazy(() => import('@/pages/admin/AccessReviews').then(m => ({ default: m.AccessReviewDetail })));
const NotFound = React.lazy(() => import('@/pages/NotFound'));
const Help = React.lazy(() => import('@/pages/Help'));

// Executive Dashboard
const ExecutiveDashboard = React.lazy(() => import('@/pages/executive/ExecutiveDashboard'));

// Report pages
const StaffPerformance = React.lazy(() => import('@/pages/reports/StaffPerformance'));

// SBL module pages
const StockPosition     = React.lazy(() => import('@/pages/sbl/StockPosition'));
const SblReconciliation = React.lazy(() => import('@/pages/sbl/Reconciliation'));
const TransactionViewer = React.lazy(() => import('@/pages/sbl/TransactionViewer'));
const SblParallelRun    = React.lazy(() => import('@/pages/sbl/ParallelRun'));
const SblMovements      = React.lazy(() => import('@/pages/sbl/Movements'));
const SblGrossProfit    = React.lazy(() => import('@/pages/sbl/GrossProfit'));
const SblUnlinked       = React.lazy(() => import('@/pages/sbl/Unlinked'));
const SblExceptions     = React.lazy(() => import('@/pages/sbl/Exceptions'));
const SblLedgerProof    = React.lazy(() => import('@/pages/sbl/LedgerProof'));
const SblDataHealth     = React.lazy(() => import('@/pages/sbl/DataHealth'));
const SblOpenDocuments  = React.lazy(() => import('@/pages/sbl/OpenDocuments'));
const StockClientRoute  = React.lazy(() => import('@/pages/sbl/StockClientRoute'));
const CloseAdjustments  = React.lazy(() => import('@/pages/close/Adjustments'));
const CloseRunDetail    = React.lazy(() => import('@/pages/close/RunDetail'));
const IcMatrix          = React.lazy(() => import('@/pages/intercompany/Matrix'));
const IcPairs           = React.lazy(() => import('@/pages/intercompany/Pairs'));
const IcPairDetail      = React.lazy(() => import('@/pages/intercompany/PairDetail'));
const IcUnassigned      = React.lazy(() => import('@/pages/intercompany/Unassigned'));
const IcHealth          = React.lazy(() => import('@/pages/intercompany/Health'));
const IcElimination     = React.lazy(() => import('@/pages/intercompany/Elimination'));
const IcDisputes        = React.lazy(() => import('@/pages/intercompany/Disputes'));
const IcApprovals       = React.lazy(() => import('@/pages/intercompany/Approvals'));
const IcUploads         = React.lazy(() => import('@/pages/intercompany/Uploads'));
const IcSetup           = React.lazy(() => import('@/pages/intercompany/Setup'));

// Bank Rec module pages
const BankRecAccountList = React.lazy(() => import('@/pages/bankrec/BankRecAccountList'));
const BankRecWorkspace   = React.lazy(() => import('@/pages/bankrec/BankRecWorkspace'));
const StatementImport    = React.lazy(() => import('@/pages/bankrec/StatementImport'));
const StatementHistoryImport = React.lazy(() => import('@/pages/bankrec/StatementHistoryImport'));
const PaymentFiles       = React.lazy(() => import('@/pages/bankrec/PaymentFiles'));
const BankRecAdmin       = React.lazy(() => import('@/pages/bankrec/BankRecAdmin'));
const BankRecSettings    = React.lazy(() => import('@/pages/bankrec/BankRecSettings'));
const CashWorkbench      = React.lazy(() => import('@/pages/cashapp/Workbench'));
const CashPosting        = React.lazy(() => import('@/pages/cashapp/Posting'));
const CashOverview       = React.lazy(() => import('@/pages/cashapp/Overview'));
const CashPerformance    = React.lazy(() => import('@/pages/cashapp/Performance'));
const CreditControl      = React.lazy(() => import('@/pages/cashapp/CreditControl'));
const OrdersOnHold       = React.lazy(() => import('@/pages/cashapp/OrdersOnHold'));     // 2 Nov: order release, Phase 1
const CashCustomerView   = React.lazy(() => import('@/pages/cashapp/CustomerView'));
const CashCustomerApply  = React.lazy(() => import('@/pages/cashapp/CustomerApply'));
const CashApplyProposal  = React.lazy(() => import('@/pages/cashapp/ApplyProposal'));
const CashApplyProposals = React.lazy(() => import('@/pages/cashapp/ApplyProposals'));
const CashFingerprints   = React.lazy(() => import('@/pages/cashapp/Fingerprints'));
const RemittanceReview   = React.lazy(() => import('@/pages/cashapp/card/ReviewRedirect'));   // Phase 4b: retired into the match card
const Inbox              = React.lazy(() => import('@/pages/cashapp/Inbox'));          // Phase 3; everyone with the cash app since 2 Nov
const InboxReports       = React.lazy(() => import('@/pages/cashapp/InboxReports'));
const RemittancesOverview = React.lazy(() => import('@/pages/cashapp/RemittancesOverview'));   // 1 Nov: Phase 1, sysadmin preview
const PayerNames         = React.lazy(() => import('@/pages/cashapp/PayerNames'));
const SettlementDiscounts = React.lazy(() => import('@/pages/cashapp/SettlementDiscounts'));
const CashWorklist       = React.lazy(() => import('@/pages/cashapp/Worklist'));
const ChaseTemplates     = React.lazy(() => import('@/pages/cashapp/ChaseTemplates'));
const Creditsafe         = React.lazy(() => import('@/pages/cashapp/Creditsafe'));
const CardSettlements    = React.lazy(() => import('@/pages/cashapp/CardSettlements'));
const CashDisputes       = React.lazy(() => import('@/pages/cashapp/Disputes'));
const CashAgedDebt       = React.lazy(() => import('@/pages/cashapp/AgedDebt'));
const CashReceiptsDayBook = React.lazy(() => import('@/pages/cashapp/ReceiptsDayBook'));
const CashParallelRun = React.lazy(() => import('@/pages/cashapp/ParallelRun'));
const CashUnapplied      = React.lazy(() => import('@/pages/cashapp/UnappliedPayments'));
const CustomerRequests   = React.lazy(() => import('@/pages/cashapp/CustomerRequests'));
const SentMail           = React.lazy(() => import('@/pages/cashapp/CustomerRequests').then(m => ({ default: m.SentMailPage })));
const OldPage            = React.lazy(() => import('@/pages/cashapp/oldPages').then(m => ({ default: m.OldPage })));
const CashActivities     = React.lazy(() => import('@/pages/cashapp/Activities'));
const CashToday          = React.lazy(() => import('@/pages/cashapp/Today'));

// Admin pages
const Users = React.lazy(() => import('@/pages/admin/Users'));
const UserAssignments = React.lazy(() => import('@/pages/admin/UserAssignments'));
const PeriodManagement = React.lazy(() => import('@/pages/admin/PeriodManagement'));
const AccountConfiguration = React.lazy(() => import('@/pages/admin/AccountConfiguration'));
const AuditTrail = React.lazy(() => import('@/pages/admin/AuditTrail'));
const EmailTemplates = React.lazy(() => import('@/pages/admin/EmailTemplates'));
const DelegationAdmin = React.lazy(() => import('@/pages/admin/DelegationAdmin'));
const TemplateManagement = React.lazy(() => import('@/pages/admin/TemplateManagement'));
const HelpAdmin = React.lazy(() => import('@/pages/admin/HelpAdmin'));
const SettingsHub = React.lazy(() => import('@/pages/admin/SettingsHub'));
const DesignSystem = React.lazy(() => import('@/pages/design/DesignSystem'));
const ApiKeys = React.lazy(() => import('@/pages/admin/ApiKeys'));
const BalanceVarianceReport = React.lazy(() => import('@/pages/admin/BalanceVarianceReport'));
const BalanceCheck = React.lazy(() => import('@/pages/admin/BalanceCheck'));
const NetSuiteWriteback = React.lazy(() => import('@/pages/admin/NetSuiteWriteback'));
const ImportQueue = React.lazy(() => import('@/pages/admin/ImportQueue'));
const AutoCertRules = React.lazy(() => import('@/pages/admin/AutoCertRules'));
const AdminCurrencies = React.lazy(() => import('@/pages/admin/Currencies'));
const BalanceSheetCompanies = React.lazy(() => import('@/pages/BalanceSheetCompanies'));
const BalanceSheetSettings = React.lazy(() => import('@/pages/admin/BalanceSheetSettings'));
const AdminJournals = React.lazy(() => import('@/pages/admin/Journals'));
const AdminJournalSettings = React.lazy(() => import('@/pages/admin/Journals').then(m => ({ default: m.JournalSettingsPage })));

// User pages
const NotificationSettings = React.lazy(() => import('@/pages/NotificationSettings'));
const NotificationHistory = React.lazy(() => import('@/pages/NotificationHistory'));
const Delegations = React.lazy(() => import('@/pages/Delegations'));
const Profile = React.lazy(() => import('@/pages/Profile'));

const App: React.FC = () => {
  const { data: authStatus, isLoading, error } = useAuthStatus();

  // Show loading screen while checking authentication
  if (isLoading) {
    return <LoadingScreen message="Checking authentication..." />;
  }

  // Show error screen if auth check failed
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Connection Error
          </h1>
          <p className="text-gray-600 mb-6">
            Unable to connect to the server. Please check your connection and try again.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="btn-primary"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Render login page if not authenticated
  if (!authStatus?.authenticated) {
    // Save current location for redirect after login (except for login and callback pages)
    const currentPath = window.location.pathname + window.location.search;
    if (currentPath !== '/login' && currentPath !== '/auth/callback' && !currentPath.startsWith('/auth/')
        && !currentPath.startsWith('/invite/')) {
      sessionStorage.setItem('authReturnUrl', currentPath);
    }
    
    return (
      <React.Suspense fallback={<LoadingScreen message="Loading login..." />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/invite/:token" element={<AcceptInvitation />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </React.Suspense>
    );
  }

  // An administrator set this user's password: only "choose a new password" until it's changed (auth Phase B)
  if (authStatus.must_change_password) {
    return (
      <ErrorBoundary>
        <React.Suspense fallback={<LoadingScreen message="Loading..." />}>
          <ChangePasswordRequired name={authStatus.user?.first_name || undefined} />
        </React.Suspense>
      </ErrorBoundary>
    );
  }

  // An integration partner sees only their own API keys (the server denies everything else anyway)
  if (isIntegrationOnly(authStatus.user?.roles)) {
    return (
      <ErrorBoundary>
        <IntegrationShell email={authStatus.user?.email} />
        <SessionTimeoutWarning />
      </ErrorBoundary>
    );
  }

  // Render main application if authenticated
  return (
    <ErrorBoundary>
      <SessionTimeoutWarning />{/* F9: warns before an idle or time-limit sign-out */}
      <ModuleProvider>
      <SubsidiaryProvider>
        <PeriodProvider>
          <RoleViewProvider>
            <ConfirmProvider>
            <Layout>
            <React.Suspense fallback={<LoadingScreen message="Loading page..." />}>
              <Routes>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<Dashboard />} />
              {/* C5: the approvals inbox; what it lists is cut per module, company and role by /api/approvals */}
              <Route path="/approvals" element={<Approvals />} />
              <Route path="/reports/executive-dashboard" element={
                <ProtectedRoute requiredRoles={['admin']}>
                  <ExecutiveDashboard />
                </ProtectedRoute>
              } />
              <Route path="/reports/staff-performance" element={
                <ProtectedRoute requiredRoles={['admin']}>
                  <StaffPerformance />
                </ProtectedRoute>
              } />
              {/* SBL module routes: open only with the module on this instance and granted (C20) */}
              <Route element={<ModuleRoute module="sbl" />}>
               <Route element={<StockClientRoute />}>{/* R1 T3: the client's currency before any Stock page */}
                <Route path="/sbl"                        element={<StockPosition />} />
                <Route path="/sbl/reconciliations/:kind"  element={<SblReconciliation />} />
                <Route path="/sbl/open-documents/:kind/:side" element={<SblOpenDocuments />} />
                <Route path="/sbl/transactions"           element={<TransactionViewer />} />
                <Route path="/sbl/transactions/:id"       element={<TransactionViewer />} />
                <Route element={<HideableRoute to="/sbl" />}>{/* Norbain's; the demo hides it (HIDE_NAV) */}
                  <Route path="/sbl/parallel-run"         element={<SblParallelRun />} />
                </Route>
                <Route path="/sbl/movements"              element={<SblMovements />} />
                <Route path="/sbl/gross-profit"           element={<SblGrossProfit />} />
                <Route path="/sbl/unlinked"               element={<SblUnlinked />} />
                <Route path="/sbl/exceptions"             element={<SblExceptions />} />
                <Route path="/sbl/ledger-proof"           element={<SblLedgerProof />} />
                <Route path="/sbl/data-health"            element={<SblDataHealth />} />
                <Route path="/sbl/grni"                   element={<Navigate to="/sbl/reconciliations/grni" replace />} />
                <Route path="/sbl/gp-reporting"           element={<Navigate to="/sbl/gross-profit" replace />} />
               </Route>
              </Route>
              {/* Intercompany: open only with the module on this instance and granted */}
              <Route element={<ModuleRoute module="intercompany" />}>
                <Route path="/intercompany"                      element={<IcMatrix />} />
                <Route path="/intercompany/pairs"                element={<IcPairs />} />
                <Route path="/intercompany/pairs/:a/:b"          element={<IcPairDetail />} />
                <Route path="/intercompany/unassigned"           element={<IcUnassigned />} />
                <Route path="/intercompany/health"               element={<IcHealth />} />
                <Route path="/intercompany/elimination"          element={<IcElimination />} />
                <Route path="/intercompany/disputes"             element={<IcDisputes />} />
                <Route path="/intercompany/approvals"            element={<IcApprovals />} />
                <Route path="/intercompany/uploads"              element={<IcUploads />} />
                <Route path="/intercompany/setup"                element={<ProtectedRoute requiredRoles={['admin', 'sysadmin']}><IcSetup /></ProtectedRoute>} />
              </Route>
              {/* Month-end close routes: open only with the module on this instance and granted (C20) */}
              <Route element={<ModuleRoute module="close" />}>
                <Route path="/close"                      element={<CloseAdjustments />} />
                <Route path="/close/runs/:id"             element={<CloseRunDetail />} />
              </Route>

              {/* Bank rec module routes: open only with the module on this instance and granted (C20) */}
              <Route element={<ModuleRoute module="bankrec" />}>
                <Route path="/bankrec"                              element={<BankRecAccountList />} />
                <Route path="/bankrec/reconciliations/:id"          element={<BankRecWorkspace />} />
                <Route path="/bankrec/reconciliations/:id/import"   element={<StatementImport />} />
                <Route path="/bankrec/payment-files"                element={<PaymentFiles />} />
                <Route path="/bankrec/upload"                       element={<StatementHistoryImport />} />
              </Route>

              {/* Cash application module routes: open only with the module on this instance and granted (C20) */}
              <Route element={<ModuleRoute module="cashapp" />}>
                <Route path="/cashapp"                              element={<CashWorkbench />} />
                {/* 2 Nov switch-on: the old list, Held emails, Received emails and Customer requests send their links to
                    the new Remittances page and the Inbox (oldPages.tsx); Customer requests stays for company-limited users */}
                <Route path="/cashapp/remittances"                  element={<OldPage />} />
                <Route path="/cashapp/remittances/new"              element={<RemittancesOverview />} />
                <Route path="/cashapp/remittances/review"           element={<RemittanceReview />} />
                <Route path="/cashapp/remittances/held"             element={<OldPage />} />
                <Route path="/cashapp/inbox"                        element={<Inbox />} />
                <Route path="/cashapp/inbox/reports"                element={<ProtectedRoute requiredRoles={['admin', 'sysadmin']}><InboxReports /></ProtectedRoute>} />
                <Route path="/cashapp/remittances/trial"            element={<OldPage />} />
                <Route path="/cashapp/remittances/emails"           element={<OldPage />} />
                <Route path="/cashapp/payer-names"                  element={<PayerNames />} />
                <Route path="/cashapp/settlement-discounts"         element={<SettlementDiscounts />} />
                <Route path="/cashapp/posting"                      element={<CashPosting />} />
                <Route path="/cashapp/overview"                     element={<CashOverview />} />
                <Route path="/cashapp/performance"                  element={<CashPerformance />} />
                <Route path="/cashapp/credit"                       element={<CreditControl />} />
                <Route path="/cashapp/orders-on-hold"               element={<OrdersOnHold />} />
                <Route path="/cashapp/worklist"                     element={<CashWorklist />} />
                <Route path="/cashapp/chase-templates"              element={<ChaseTemplates />} />
                <Route path="/cashapp/creditsafe"                   element={<Creditsafe />} />
                <Route path="/cashapp/delegations"                  element={<Delegations />} />   {/* 3 Nov: cover (cover.py) */}
                <Route path="/cashapp/card-settlements"             element={<CardSettlements />} />
                <Route path="/cashapp/disputes"                     element={<CashDisputes />} />
                <Route path="/cashapp/aged-debt"                    element={<CashAgedDebt />} />
                <Route path="/cashapp/receipts-day-book"            element={<CashReceiptsDayBook />} />
                <Route path="/cashapp/reports/scorecard"            element={<CashParallelRun />} />
                <Route path="/cashapp/reports/parallel-run"         element={<OldPage />} />
                <Route path="/cashapp/unapplied-payments"           element={<CashUnapplied />} />
                <Route path="/cashapp/customer-requests"            element={<OldPage stay={<CustomerRequests />} />} />
                <Route path="/cashapp/sent-mail"                    element={<SentMail />} />
                <Route path="/cashapp/activities"                   element={<CashActivities />} />
                <Route path="/cashapp/today"                        element={<CashToday />} />
                <Route path="/cashapp/customers/:code"              element={<CashCustomerView />} />
                <Route path="/cashapp/customers/:code/apply"        element={<CashCustomerApply />} />
                <Route path="/cashapp/apply-proposals"              element={<CashApplyProposals />} />
                <Route path="/cashapp/apply-proposals/:id"          element={<CashApplyProposal />} />
                <Route path="/cashapp/fingerprints"                element={<CashFingerprints />} />
              </Route>

              <Route path="/accounts" element={<AccountsMatrix />} />
              <Route path="/balance-sheet" element={<BalanceSheet />} />
              <Route path="/balance-sheet/companies" element={<BalanceSheetCompanies />} />
              <Route path="/reconciliation" element={<Reconciliation />} />
              <Route path="/reconciliation/:id" element={<ReconciliationDetail />} />
              
              {/* Help Routes */}
              <Route path="/help" element={<Help />} />
              <Route path="/help/:categorySlug" element={<Help />} />
              <Route path="/help/:categorySlug/:pageSlug" element={<Help />} />
              
              {/* User Routes */}
              <Route path="/profile" element={<Profile />} />
              <Route path="/settings/notifications" element={<NotificationSettings />} />
              <Route path="/notifications/history" element={<NotificationHistory />} />
              <Route path="/delegations" element={<Delegations />} />
              
              {/* Admin Routes - Protected */}
              {/* C67: Settings, the hub of the admin pages (the menu's Admin group moved here) */}
              <Route path="/admin" element={
                <ProtectedRoute requiredRoles={['admin']}>
                  <SettingsHub />
                </ProtectedRoute>
              } />
              <Route path="/admin/sign-in-security" element={
                <ProtectedRoute requiredRoles={['admin']}>
                  <SignInSecurity />
                </ProtectedRoute>
              } />
              <Route path="/admin/mail-domains" element={
                <ProtectedRoute requiredRoles={['admin']}>
                  <MailAllowedDomains />
                </ProtectedRoute>
              } />
              <Route path="/admin/users" element={
                <ProtectedRoute requiredRoles={['admin']}>
                  <Users />
                </ProtectedRoute>
              } />
              <Route path="/admin/assignments" element={
                <ProtectedRoute requiredRoles={['admin']}>
                  <UserAssignments />
                </ProtectedRoute>
              } />
              <Route path="/admin/periods" element={
                <ProtectedRoute requiredRoles={['admin']}>
                  <PeriodManagement />
                </ProtectedRoute>
              } />
              <Route path="/admin/accounts" element={
                <ProtectedRoute requiredRoles={['admin']}>
                  <AccountConfiguration />
                </ProtectedRoute>
              } />
              <Route path="/admin/access-reviews" element={
                <ProtectedRoute requiredRoles={['admin']}>
                  <AccessReviewList />
                </ProtectedRoute>
              } />
              <Route path="/admin/access-reviews/:reviewId" element={
                <ProtectedRoute requiredRoles={['admin']}>
                  <AccessReviewDetail />
                </ProtectedRoute>
              } />
              <Route path="/admin/audit-pack" element={
                <ProtectedRoute requiredRoles={['admin']}>
                  <AuditPacks />
                </ProtectedRoute>
              } />
              <Route path="/admin/email-labels" element={
                <ProtectedRoute requiredRoles={['admin']}>
                  <EmailLabels />
                </ProtectedRoute>
              } />
              <Route path="/admin/audit" element={
                <ProtectedRoute requiredRoles={['admin', 'auditor']}>{/* K-07: auditors read it, read-only */}
                  <AuditTrail />
                </ProtectedRoute>
              } />
              <Route path="/admin/email-templates" element={
                <ProtectedRoute requiredRoles={['admin']}>
                  <EmailTemplates />
                </ProtectedRoute>
              } />
              <Route path="/admin/delegations" element={
                <ProtectedRoute requiredRoles={['admin']}>
                  <DelegationAdmin />
                </ProtectedRoute>
              } />
              <Route path="/admin/templates" element={
                <ProtectedRoute requiredRoles={['admin']}>
                  <TemplateManagement />
                </ProtectedRoute>
              } />
              <Route path="/admin/api-keys" element={
                <ProtectedRoute requiredRoles={['admin']}>
                  <ApiKeys />
                </ProtectedRoute>
              } />
              <Route path="/admin/balance-check" element={
                <ProtectedRoute requiredRoles={['admin']}>
                  <BalanceCheck />
                </ProtectedRoute>
              } />
              <Route path="/admin/netsuite-writeback" element={
                <ProtectedRoute requiredRoles={['admin']}>
                  <NetSuiteWriteback />
                </ProtectedRoute>
              } />
              <Route path="/admin/balance-variance" element={
                <ProtectedRoute requiredRoles={['admin']}>
                  <BalanceVarianceReport />
                </ProtectedRoute>
              } />
              <Route path="/admin/import-queue" element={
                <ProtectedRoute requiredRoles={['admin']}>
                  <ImportQueue />
                </ProtectedRoute>
              } />
              <Route path="/admin/auto-cert-rules" element={
                <ProtectedRoute requiredRoles={['admin']}>
                  <AutoCertRules />
                </ProtectedRoute>
              } />
              <Route path="/admin/journal-settings" element={
                <ProtectedRoute requiredRoles={['admin']}>
                  <AdminJournalSettings />
                </ProtectedRoute>
              } />
              <Route path="/admin/journals" element={
                <ProtectedRoute requiredRoles={['admin', 'approver', 'senior_approver']}>
                  <AdminJournals />
                </ProtectedRoute>
              } />
              <Route path="/admin/balance-sheet-settings" element={
                <ProtectedRoute requiredRoles={['admin']}>
                  <BalanceSheetSettings />
                </ProtectedRoute>
              } />
              <Route path="/admin/currencies" element={
                <ProtectedRoute requiredRoles={['admin', 'sysadmin']}>
                  <AdminCurrencies />
                </ProtectedRoute>
              } />
              <Route path="/admin/bank-accounts" element={
                <ModuleRoute module="bankrec">
                  <ProtectedRoute requiredRoles={['admin']}>
                    <BankRecAdmin />
                  </ProtectedRoute>
                </ModuleRoute>
              } />
              <Route path="/admin/customer-requests" element={
                <ModuleRoute module="cashapp">
                  <ProtectedRoute requiredRoles={['admin']}>
                    <CustomerRequestSettings />
                  </ProtectedRoute>
                </ModuleRoute>
              } />
              <Route path="/admin/remittance-feed-health" element={
                <ModuleRoute module="cashapp">
                  <ProtectedRoute requiredRoles={['admin']}>
                    <FeedHealthPage />
                  </ProtectedRoute>
                </ModuleRoute>
              } />
              <Route path="/admin/credit-controllers" element={
                <ModuleRoute module="cashapp">
                  <ProtectedRoute requiredRoles={['admin']}>
                    <CreditControllers />
                  </ProtectedRoute>
                </ModuleRoute>
              } />
              <Route path="/admin/group-companies" element={
                <ModuleRoute module="cashapp">
                  <ProtectedRoute requiredRoles={['sysadmin']}>
                    <GroupCompanies />
                  </ProtectedRoute>
                </ModuleRoute>
              } />
              <Route path="/admin/cashapp-automation" element={
                <ModuleRoute module="cashapp">
                  <ProtectedRoute requiredRoles={['admin']}>
                    <CashappAutomation />
                  </ProtectedRoute>
                </ModuleRoute>
              } />
              <Route path="/admin/bankrec-settings" element={
                <ModuleRoute module="bankrec">
                  <ProtectedRoute requiredRoles={['admin']}>
                    <BankRecSettings />
                  </ProtectedRoute>
                </ModuleRoute>
              } />

              {/* UX Stage B: the candidate looks, for choosing one. Admin only, not in the nav. */}
              <Route path="/design" element={
                <ProtectedRoute requiredRoles={['admin']}>
                  <DesignSystem />
                </ProtectedRoute>
              } />

              {/* Help Admin Routes - Protected */}
              <Route path="/help/admin" element={
                <ProtectedRoute requiredRoles={['admin']}>
                  <HelpAdmin />
                </ProtectedRoute>
              } />
              {/* Help is published from content files; the old editor routes go to the (read-only) admin page */}
              <Route path="/help/admin/pages/*" element={<Navigate to="/help/admin" replace />} />
              
              <Route path="/login" element={<Navigate to="/dashboard" replace />} />
              {/* An invitation accepted (or opened while signed in): carry on into the app */}
              <Route path="/invite/*" element={<Navigate to="/dashboard" replace />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </React.Suspense>
            </Layout>
            </ConfirmProvider>
          </RoleViewProvider>
        </PeriodProvider>
      </SubsidiaryProvider>
      </ModuleProvider>
    </ErrorBoundary>
  );
};

export default App;